// Define TypeScript interfaces for our data structures
interface RatingRange {
  min: number;
  max: number;
  name: string;
}

interface ColorSet {
  bg: string;
  text: string;
  border: string;
  indicator: string;
}

interface RatingInfo {
  category: string;
  name: string;
  description: string;
  colors: ColorSet;
  score: number;
}

type RatingCategory = 'HIGH' | 'MODERATE' | 'LOW' | 'CRITICAL';
export type ColorType = 'bg' | 'text' | 'border' | 'indicator';

// Rating ranges
const RATINGS: Record<RatingCategory, RatingRange> = {
  HIGH: { min: 8, max: 10, name: 'High Agility' },
  MODERATE: { min: 5, max: 7, name: 'Moderate Agility' },
  LOW: { min: 3, max: 5, name: 'Low Agility' },
  CRITICAL: { min: 1, max: 3, name: 'Critical Agility Gap' }
};

const COLORS: Record<RatingCategory, ColorSet> = {
  HIGH: {
    bg: 'bg-[#82ca74]',
    text: 'text-[#82ca74]',
    border: 'border-[#82ca74]',
    indicator: 'green'
  },
  MODERATE: {
    bg: 'bg-[#ffd966]',
    text: 'text-[#ffd966]',
    border: 'border-[#ffd966]',
    indicator: 'yellow'
  },
  LOW: {
    bg: 'bg-[#f8a154]',
    text: 'text-[#f8a154]',
    border: 'border-[#f8a154]',
    indicator: 'orange'
  },
  CRITICAL: {
    bg: 'bg-[#e04f5f]',
    text: 'text-[#e04f5f]',
    border: 'border-[#e04f5f]',
    indicator: 'red'
  }
};

const DESCRIPTIONS: Record<RatingCategory, string> = {
  HIGH: 'The enabler is performing exceptionally well and requires minimal intervention.',
  MODERATE: 'The enabler has satisfactory performance but opportunities for improvement exist. Some adjustments may enhance agility.',
  LOW: 'The enabler shows weaknesses that could hinder innovation and responsiveness. Improvements are strongly recommended.',
  CRITICAL: 'Major gaps in agility. Urgent action needed to prevent negative impacts on adaptability and resilience.'
};

/**
 * Get rating category based on score
 * @param {number} score - The agility score
 * @returns {RatingCategory} - Rating category
 */
function getRatingCategory(score: number): RatingCategory {
  if (score >= RATINGS.HIGH.min) return 'HIGH';
  if (score >= RATINGS.MODERATE.min) return 'MODERATE';
  if (score >= RATINGS.LOW.min) return 'LOW';
  return 'CRITICAL';
}

/**
 * Get color classes for a specific score
 * @param {number} score - The agility score
 * @param {ColorType} type - The type of color class (bg, text, border, indicator)
 * @returns {string} - CSS class for the specified type and score
 */
export function getColorClass(score: number, type: ColorType = 'border'): string {
  const category = getRatingCategory(score);
  return COLORS[category][type] || '';
}

/**
 * Get full rating information for a score
 * @param {number} score - The agility score
 * @returns {RatingInfo} - Complete rating information
 */
export function getRatingInfo(score: number): RatingInfo {
  const category = getRatingCategory(score);
  return {
    category,
    name: RATINGS[category].name,
    description: DESCRIPTIONS[category],
    colors: COLORS[category],
    score
  };
}

export default {
  RATINGS,
  COLORS,
  DESCRIPTIONS,
  getColorClass,
  getRatingInfo
}; 